import { useCallback, useMemo } from "react";
import { setAnonymousLikedToast, loadAnonymousLikedToast } from "@/common/utils/storage";
import { AxiosError } from "axios";
import { IErrorData } from "@/common/services/types";
import { useVisitorInfo } from "@/common/hooks/useVisitorInfo";
import { applyAnimation, AnimationClass } from "../utils/helpers";
import { showNotification } from "@/components/notifications/Notifications";
import { EventSource, metricsBus } from "@/common/buses";
import { useAuthNavigate } from "@/common/hooks/useAuthNavigate";
import { useLikesStore } from "@/common/store/likesStore";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { apiMain } from "@/common/services";

interface IUseLikeActionsProps {
  gifId: string;
  source: EventSource;
  sourceId: string | null;
  initialLikesCount: number;
  ref: React.RefObject<HTMLDivElement | null>;
  boosted: boolean;
  position: number;
  playCount: number;
}

const STATUS_401 = 401;

export const useLikeActions = ({
  gifId,
  source,
  sourceId,
  ref,
  boosted,
  position,
  playCount
}: IUseLikeActionsProps) => {
  const likes = useLikesStore((state) => state.likes);
  const setLike = useLikesStore((state) => state.setLike);
  const setUnLike = useLikesStore((state) => state.setUnLike);
  const checkLiked = useLikesStore((state) => state.checkLiked);
  const gifContext = useAnalyticsStore((state) => state.gifContext);

  const currentUser = useVisitorInfo();
  const { navigateSignIn } = useAuthNavigate();

  const showToast = useCallback(() => {
    if (!currentUser && !loadAnonymousLikedToast()) {
      showNotification({
        variant: "success",
        title: "Please log in to make sure your liked gifs aren’t lost"
      });
      setAnonymousLikedToast(true);
    }
  }, [currentUser]);

  const handleUnauthorized = useCallback((e: AxiosError<IErrorData>) => {
    if (e?.response?.status === STATUS_401) {
      navigateSignIn();
    }
  }, []);

  const handleAddLike = useCallback(async () => {
    setLike(gifId);
    applyAnimation({ ref, animationClass: AnimationClass.Liked });

    try {
      await apiMain.likeGifById({ gifId, context: gifContext });
      metricsBus.emit("gifLiked", {
        gifId,
        isMainGIF: position === 1,
        source,
        sourceId,
        boosted,
        position,
        playCount,
        userName: currentUser?.username,
      });
      showToast();
      console.debug(`Gif ${gifId} liked.`);
    } catch (e) {
      console.error(`Gif ${gifId} could not be liked: ${e}.`);
      setUnLike(gifId);
      handleUnauthorized(e as AxiosError<IErrorData>);
    }
  }, [gifId, sourceId, gifContext, showToast, handleUnauthorized, ref, boosted, position, playCount]);

  const handleRemoveLike = useCallback(async () => {
    setUnLike(gifId);

    try {
      await apiMain.unlikeGifById({ gifId, context: gifContext });
      metricsBus.emit("gifUnliked", { gifId, isMainGIF: position === 1 });
      console.debug(`Gif ${gifId} unliked.`);
    } catch (e) {
      console.error(`Gif ${gifId} could not be unliked: ${e}.`);
      setLike(gifId);
      handleUnauthorized(e as AxiosError<IErrorData>);
    }
  }, [gifId, gifContext, handleUnauthorized, ref]);

  const liked = useMemo(() => checkLiked(gifId), [likes, gifId]);

  const handleLike = useCallback(() => {
    if (liked) {
      handleRemoveLike();
    } else {
      handleAddLike();
    }
  }, [liked, handleRemoveLike, handleAddLike]);

  return {
    liked,
    handleLike,
  };
};
